/**
 * The wrapper that keeps a self-service session out of the staff surfaces — by not rendering them.
 *
 * WHY A REDIRECT AND NOT A HIDDEN ELEMENT. Three of the four things a staff page draws are useless
 * to a player and one is actively misleading: the directory search box would return their own row
 * and look broken, the grant link points at a route the sidecar refuses, and `/player/<somebody
 * else>` is exactly the enumerable URL the player tier exists to make meaningless. `display: none`
 * would leave all of it in the DOM, one devtools toggle from being pressed, and would leave the
 * page's own requests firing. So a non-staff session that lands on a staff route is sent to the phone,
 * the pages that are theirs, and the staff markup never mounts at all — which is what the Playwright
 * spec asserts (element count zero, not visibility).
 *
 * IT IS NOT THE ENFORCEMENT AND MUST NEVER BE READ AS ONE. A player who edits the role in their own
 * sessionStorage renders every staff page in this SPA and reads nothing new: the sidecar verifies
 * the SIGNED copy of that token, switches to `web_player`, and Postgres filters every row against
 * the `uuid` claim. This component decides what is worth drawing; the database decides what comes
 * back.
 *
 * SIGNED OUT IS NOT NON-STAFF. With no session at all the children render and each page shows its
 * own sign-in prompt (which issues no requests) — the alternative would send an admin who opened a
 * bookmarked `/player/<uuid>` to a stranger's link page instead of to a sign-in they can complete.
 */
import { Navigate, Outlet } from 'react-router';

import { isStaff } from '@dimensionscore/shared';

import { useSession } from './SessionProvider';

/**
 * Where a non-staff session is sent instead: the Rotom Phone's home screen (plan §9.11), whose apps —
 * Music, Stats, the Map — are the pages players come for. Kept equal to `phone/apps.ts`'s PHONE_HOME.
 */
export const SELF_SERVICE_HOME = '/phone';

export function StaffOnly({ children }: { children: React.ReactNode }): React.JSX.Element {
  const { session } = useSession();
  if (session !== null && !isStaff(session.claims.role)) {
    // `replace` so the back button does not bounce them between the two.
    return <Navigate to={SELF_SERVICE_HOME} replace />;
  }
  return <>{children}</>;
}

/**
 * The same guard as a PATHLESS LAYOUT ROUTE, which is how `App.tsx` applies it to every staff route
 * at once.
 *
 * One wrapper around a group beats one wrapper per route for the reason a whitelist beats a
 * blacklist: a staff route added inside the group is guarded by having been put there, and a route
 * added outside it is visibly outside it. Per-route wrapping invites the ninth one to be forgotten,
 * and a forgotten one is a page a player-tier browser mounts and fires requests from.
 */
export function StaffRoutes(): React.JSX.Element {
  return (
    <StaffOnly>
      <Outlet />
    </StaffOnly>
  );
}
